import type { DefinitionsRecord, EntityDefinitions } from '@/types/eds/Definitions';
import type { RawEntityDefinition } from '@/types/eds/RawDefinitions';
import { EdsEntityCodes } from '@/features/eds/enums/EdsEntityCodes';
import { ENTITY_TIME_STAMP_KEY } from '@/features/eds/services/constants/entityTimeStampKey';
import { LocalStorageVariables } from '@/enums/LocalStorageVariables';

export class EdsStorageService {
  public static getEntity(
    entityCode: EdsEntityCodes,
    section?: string,
  ): { entity: EntityDefinitions | null; timeStamp: number | null } {
    const entities = EdsStorageService.getAllEntities();

    const entityKey = section ? [entityCode, section].join('_') : entityCode;

    const entity = entities[entityKey] as EntityDefinitions | null;
    const timeStamp = entity?.[ENTITY_TIME_STAMP_KEY] || null;

    if (entity && ENTITY_TIME_STAMP_KEY in entity) delete entity[ENTITY_TIME_STAMP_KEY];

    return { entity, timeStamp };
  }

  public static getRawEntity(
    entityCode: EdsEntityCodes,
    section?: string,
  ): {
    entity: RawEntityDefinition | null;
    timeStamp: number | null;
  } {
    const rawDefinitions = EdsStorageService.getAllRawEntities();

    const entityKey = section ? [entityCode, section].join('_') : entityCode;

    const entity = rawDefinitions[entityKey] as RawEntityDefinition | null;
    const timeStamp = entity?.[ENTITY_TIME_STAMP_KEY] || null;

    if (entity && ENTITY_TIME_STAMP_KEY in entity) delete entity[ENTITY_TIME_STAMP_KEY];

    return { entity, timeStamp };
  }

  public static getAllEntities(): DefinitionsRecord {
    EdsStorageService.init();
    const rawData = localStorage.getItem(LocalStorageVariables.Definitions);
    return rawData ? (JSON.parse(rawData) as DefinitionsRecord) : {};
  }

  public static getAllRawEntities(): Record<string, RawEntityDefinition> {
    EdsStorageService.initRaw();
    const rawData = localStorage.getItem(LocalStorageVariables.RawDefinitions);
    return rawData ? (JSON.parse(rawData) as Record<string, RawEntityDefinition>) : {};
  }

  public static set(entityCode: EdsEntityCodes, value: EntityDefinitions, section?: string) {
    const entities = EdsStorageService.getAllEntities();

    const entityKey = section ? [entityCode, section].join('_') : entityCode;

    localStorage.setItem(
      LocalStorageVariables.Definitions,
      JSON.stringify({
        ...entities,
        [entityKey]: {
          ...value,
          [ENTITY_TIME_STAMP_KEY]: new Date().getTime(),
        },
      }),
    );
  }

  public static setRawEntity(entityCode: EdsEntityCodes, value: RawEntityDefinition, section?: string) {
    const rawDefinitions = EdsStorageService.getAllRawEntities();

    const entityKey = section ? [entityCode, section].join('_') : entityCode;

    localStorage.setItem(
      LocalStorageVariables.RawDefinitions,
      JSON.stringify({
        ...rawDefinitions,
        [entityKey]: {
          ...value,
          [ENTITY_TIME_STAMP_KEY]: new Date().getTime(),
        },
      }),
    );
  }

  public static updateEntity(entityCode: EdsEntityCodes, value: Partial<EntityDefinitions>, section?: string) {
    EdsStorageService.init();
    const entities = EdsStorageService.getAllEntities();
    const { entity } = EdsStorageService.getEntity(entityCode);

    const entityKey = section ? [entityCode, section].join('_') : entityCode;

    localStorage.setItem(
      LocalStorageVariables.Definitions,
      JSON.stringify({
        ...entities,
        [entityKey]: {
          ...(entity || {}),
          ...value,
          [ENTITY_TIME_STAMP_KEY]: new Date().getTime(),
        },
      }),
    );
  }

  public static removeDefinition(keyPart: string) {
    EdsStorageService.init();
    const entitiesRecord = EdsStorageService.getAllEntities();
    const processedRecord = Object.keys(entitiesRecord).reduce((acc, entityKey) => {
      if (entityKey === keyPart) return acc;
      return { ...acc, [entityKey]: entitiesRecord[entityKey] };
    }, {} as DefinitionsRecord);
    localStorage.setItem(LocalStorageVariables.Definitions, JSON.stringify(processedRecord));
  }

  public static removeRawDefinition(keyPart: string) {
    EdsStorageService.init();
    const rawEntitiesRecord = EdsStorageService.getAllRawEntities();
    const rawEntities = Object.keys(rawEntitiesRecord).reduce(
      (acc, entityKey) => {
        if (entityKey === keyPart) return acc;
        return { ...acc, [entityKey]: rawEntitiesRecord[entityKey] };
      },
      {} as Record<string, RawEntityDefinition>,
    );
    localStorage.setItem(LocalStorageVariables.RawDefinitions, JSON.stringify(rawEntities));
  }

  private static init() {
    if (!localStorage.getItem(LocalStorageVariables.Definitions))
      localStorage.setItem(LocalStorageVariables.Definitions, JSON.stringify({}));
  }

  private static initRaw() {
    if (!localStorage.getItem(LocalStorageVariables.RawDefinitions))
      localStorage.setItem(LocalStorageVariables.RawDefinitions, JSON.stringify({}));
  }
}
